import { standingOf } from '@novachat/client';
import type { Channel, Me } from '@novachat/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, MessageSquare, MicOff, PhoneOff, Volume2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { firstName } from '../lib/avatar';
import { client } from '../lib/client';
import { errorMessage } from '../lib/errors';
import { mentionInComposer, useComposer } from '../state/composer';
import { useLive } from '../state/live';
import { useProfile, type ProfileTarget } from '../state/profile';
import { useVoice } from '../state/voice';
import { Avatar, presenceText } from './Avatar';
import { useChannels } from './Sidebar';

/**
 * A person's name or avatar that opens their profile card. Inside the pop-out call window it does
 * nothing: the card belongs to the main window.
 */
export function ProfileButton({ user, label, className = '', children }: { user: ProfileTarget; label?: string; className?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      data-profile-trigger=""
      aria-haspopup="dialog"
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        if (event.currentTarget.ownerDocument !== document) return;
        useProfile.getState().toggle(user, event.currentTarget);
      }}
      className={`cursor-pointer border-0 bg-transparent p-0 text-left text-inherit ${className}`}
    >
      {children}
    </button>
  );
}

const CARD_WIDTH = 300;
const GAP = 8;

/** The open profile card; next to the clicked name on computers, from the bottom on phones. */
export function ProfileCard({ me }: { me: Me }) {
  const user = useProfile((s) => s.user);
  const from = useProfile((s) => s.from);
  if (!user || !from) return null;
  return <Card key={`${user.id}`} user={user} from={from} me={me} />;
}

function Card({ user, from, me }: { user: ProfileTarget; from: HTMLElement; me: Me }) {
  const ref = useRef<HTMLDivElement>(null);
  const queries = useQueryClient();
  const [place, setPlace] = useState<CSSProperties>({ visibility: 'hidden' });
  const [error, setError] = useState<string | null>(null);
  const presence = useLive((s) => s.presence[user.id] ?? 'offline');
  const rooms = useLive((s) => s.voice);
  const { data: channels } = useChannels();
  const myCall = useVoice((s) => s.channelId);
  const volume = useVoice((s) => s.volumes[user.id] ?? 1);
  const composer = useComposer((s) => s.channelId);
  const self = user.id === me.id;
  // Only calls in channels you can see (the server sends no others).
  const callId = Object.entries(rooms).find(([, people]) => people.some((p) => p.userId === user.id))?.[0];
  const call = channels?.find((c) => c.id === callId);
  const hide = useProfile.getState().hide;
  // Moderators mute or remove someone below them in the call (docs/plans/2026-11-03-channel-roles-design.md).
  const moderating = !!call && !self && call.kind !== 'dm' && call.permissions.includes('voice_moderate');
  const roles = useQuery({ queryKey: ['roles', call?.id], queryFn: () => client.api.roles(call!.id), enabled: moderating });
  const canModerate = moderating && !!roles.data && standingOf(call, me, roles.data).below(user.id);
  const theirMic = !!callId && rooms[callId]?.find((p) => p.userId === user.id)?.micOn;
  async function moderate(action: () => Promise<unknown>, done: boolean) {
    setError(null);
    try {
      await action();
      if (done) hide();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  // Beside the name, flipped or pushed to stay on screen; phones use the bottom sheet (CSS).
  useLayoutEffect(() => {
    const card = ref.current;
    if (!card) return;
    if (window.innerWidth < 768) return setPlace({});
    const anchor = from.getBoundingClientRect();
    const height = card.offsetHeight;
    const fitsRight = anchor.right + GAP + CARD_WIDTH <= window.innerWidth - GAP;
    const fitsLeft = anchor.left - GAP - CARD_WIDTH >= GAP;
    const left = fitsRight ? anchor.right + GAP : fitsLeft ? anchor.left - GAP - CARD_WIDTH : Math.max(GAP, Math.min(anchor.left, window.innerWidth - CARD_WIDTH - GAP));
    const below = !fitsRight && !fitsLeft;
    const top = Math.max(GAP, Math.min(below ? anchor.bottom + GAP : anchor.top - 12, window.innerHeight - height - GAP));
    setPlace({ left, top });
  }, [from]);

  // Escape, a click elsewhere, or the name it points at moving away (scroll, resize) closes it.
  useEffect(() => {
    ref.current?.focus();
    const start = from.getBoundingClientRect();
    const down = (event: PointerEvent) => {
      const target = event.target as Element;
      if (!ref.current?.contains(target) && !target.closest?.('[data-profile-trigger]')) hide();
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      hide();
      from.focus();
    };
    // Lists also scroll by themselves (new messages, "yazıyor…"): only a real move of the name counts.
    const moved = () => {
      const now = from.getBoundingClientRect();
      if (!from.isConnected || Math.abs(now.top - start.top) > 4 || Math.abs(now.left - start.left) > 4) hide();
    };
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', moved, true);
    window.addEventListener('resize', moved);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', moved, true);
      window.removeEventListener('resize', moved);
    };
  }, [from, hide]);

  async function message() {
    try {
      const dm = await client.api.openDm(user.id);
      queries.setQueryData<Channel[]>(['channels'], (list = []) => (list.some((c) => c.id === dm.id) ? list : [...list, dm]));
      useLive.getState().select(dm.id);
      hide();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function joinCall(channelId: string) {
    useLive.getState().select(channelId);
    void useVoice.getState().join(channelId);
    hide();
  }

  const action = 'nc-press flex h-9 items-center justify-center gap-1.5 rounded-sm px-3 text-sm font-semibold';
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={`${user.name} profili`}
      tabIndex={-1}
      style={place}
      className="fixed z-50 w-[300px] animate-enter rounded-lg border border-line-strong bg-surface-2 p-4 shadow-2xl outline-none max-md:inset-x-3 max-md:bottom-[calc(12px+env(safe-area-inset-bottom))] max-md:w-auto"
    >
      <div className="flex items-center gap-3">
        <Avatar id={user.id} name={user.name} size={56} presence={presence} />
        <div className="min-w-0">
          <h2 className="m-0 truncate font-heading text-lg font-bold">
            {user.name}
            {self && <span className="ml-1.5 text-sm font-medium text-fg-3">(sen)</span>}
          </h2>
          <p className="m-0 text-[13px] text-fg-2">{presenceText(presence)}</p>
        </div>
      </div>

      {call && (
        <div className="mt-3 flex items-center gap-2 rounded-md bg-surface-3 px-3 py-2 text-[13px]">
          <Volume2 size={15} className="flex-none text-green-400" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">
            {myCall === call.id && !self && 'Birlikte '}
            <span className="font-semibold">#{call.name || call.peer?.name}</span> {myCall === call.id && !self ? 'görüşmesindesiniz' : 'görüşmesinde'}
          </span>
          {myCall !== call.id && (
            <button type="button" onClick={() => joinCall(call.id)} className="nc-press flex-none rounded-sm border-0 bg-brand-500 px-2.5 py-1 text-[13px] font-bold text-white">
              Katıl
            </button>
          )}
        </div>
      )}

      {!self && (
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={() => void message()} className={`${action} nc-cta flex-1 border-0 bg-brand-500 text-white`}>
            <MessageSquare size={16} aria-hidden="true" />
            Mesaj gönder
          </button>
          {composer && (
            <button
              type="button"
              onClick={() => {
                mentionInComposer(user.name);
                hide();
              }}
              className={`${action} border border-line-strong bg-surface-3 text-fg-1`}
            >
              <AtSign size={16} aria-hidden="true" />
              Bahset
            </button>
          )}
        </div>
      )}

      {canModerate && (
        <div className="mt-2 flex gap-2">
          {theirMic && (
            <button
              type="button"
              onClick={() => void moderate(() => client.api.muteInCall(call.id, user.id), false)}
              className={`${action} flex-1 border border-line-strong bg-surface-3 text-fg-1`}
            >
              <MicOff size={16} aria-hidden="true" />
              Sustur
            </button>
          )}
          <button
            type="button"
            aria-label="Görüşmeden çıkar"
            title="Görüşmeden çıkar"
            onClick={() => void moderate(() => client.api.removeFromCall(call.id, user.id), true)}
            className={`${action} flex-1 border border-line-strong bg-surface-3 text-[#EE9191]`}
          >
            <PhoneOff size={16} aria-hidden="true" />
            Çıkar
          </button>
        </div>
      )}

      {!self && myCall !== null && myCall === callId && (
        <label className="mt-3 flex items-center gap-3 text-[13px] text-fg-2">
          <Volume2 size={15} className="flex-none" aria-hidden="true" />
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(volume * 100)}
            aria-label={`${firstName(user.name)} ses seviyesi`}
            onChange={(e) => useVoice.getState().setVolume(user.id, Number(e.target.value) / 100)}
            className="min-w-0 flex-1 accent-brand-500"
          />
          <span className="w-10 flex-none text-right tabular-nums">%{Math.round(volume * 100)}</span>
        </label>
      )}

      {error && (
        <p role="alert" className="m-0 mt-2 text-[12.5px] text-[#EE9191]">
          {error}
        </p>
      )}
    </div>
  );
}
